import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../store";

import UploadMessages from "../../../components/UploadMessages";
import ScrewDetails from "../../../components/Details/ScrewDetails";
import ScrewsGrid from "../../../components/Details/ScrewsGrid";
import FileUpload from "../../../components/FileUpload";
import Loader from "../../../components/Loader";
import { resetScrewUpload } from "../../../store/reducers/ScrewsReducers";
import { getScrewUploadStatus } from "../../../store/selectors/ScrewSelectors";
import {
    getUploadStep,
    getIsUploadStarted,
  } from "../../../store/selectors/DatasetSelectors";
import { uploadScrewImagesAction } from "../../../store/actions/ScrewsActions";

import {
  resetDatasetSlice,
  setIsUploadStarted,
  updateDatasetStep,
} from "../../../store/reducers/DatasetReducer";

const ScrewDataset = () => {
  const dispatch = useAppDispatch();

  const screwUploadStep = useAppSelector(getUploadStep);
  const isUploadStarted = useAppSelector(getIsUploadStarted);
  const screwUploadStatus = useAppSelector(getScrewUploadStatus);

  const [selectedFiles, setSelectedFiles] = useState<any>(null);

  const onCancelUpload = useCallback(() => {
    dispatch(setIsUploadStarted(false));
  }, [dispatch]);

  const handleFileSubmission = useCallback(() => {
    if (!selectedFiles) return;

    const formData = new (globalThis as any).FormData();
    if (Array.isArray(selectedFiles)) {
      selectedFiles.forEach((file: any) => {
        formData.append("images", file, file.name);
      });
    } else {
      formData.append("images", selectedFiles, selectedFiles.name);
    }
    dispatch(uploadScrewImagesAction(formData));
  }, [selectedFiles, dispatch]);

  const fileUploadProps = useCallback(
    () => ({
      isFileUploading: screwUploadStatus === "loading",
      selectedFile: selectedFiles,
      onFileChange: setSelectedFiles,
      onCancelUpload,
      handleFileSubmission,
      modelType: "screw",
      allowMultiple: true, // Dataset uploads should allow multiple files
    }),
    [screwUploadStatus, selectedFiles, handleFileSubmission, onCancelUpload]
  );

  useEffect(() => {
    if (screwUploadStatus !== "success") return;
    dispatch(updateDatasetStep(2));
  }, [screwUploadStatus, dispatch]);

  useEffect(() => {
    return function () {
      if (!isUploadStarted) return;
      dispatch(resetDatasetSlice());
      dispatch(resetScrewUpload());
    };
  }, [dispatch, isUploadStarted]);

  const onClickAway = () => {
    dispatch(updateDatasetStep(1));
    dispatch(resetScrewUpload());
    setSelectedFiles(null);
  };

  const loadingMessage = <Loader />;

  const successMessage = (
    <UploadMessages
      msgType="success"
      message="Successfully added to dataset!"
      onClickForward={onClickAway}
    />
  );

  const errorMessage = (
    <UploadMessages
      msgType="error"
      message="Oops, something went wrong..."
      onClickBack={onClickAway}
    />
  );

  const defaultView =
    screwUploadStatus === "error"
      ? errorMessage
      : screwUploadStatus === "success"
      ? successMessage
      : loadingMessage;

  const datasetContent = useCallback(() => {
    if (!isUploadStarted) return <ScrewsGrid />;

    switch (screwUploadStep) {
      case 1:
        return <FileUpload {...fileUploadProps()} />;
      case 2:
        return <ScrewDetails />;
      default:
        return defaultView;
    }
  }, [isUploadStarted, screwUploadStep, defaultView, fileUploadProps]);

  return datasetContent();

};

export default ScrewDataset;
